// =============================================================================
// Details Step — form dati cliente
// =============================================================================

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from './PrimaryButton';

export interface CustomerDetails {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  fiscalCode?: string;
  dateOfBirth?: string;
  notes?: string;
}

interface Props {
  initial: CustomerDetails | null;
  onBack: () => void;
  onSubmit: (data: CustomerDetails) => void;
}

export function DetailsStep({ initial, onBack, onSubmit }: Props) {
  const { t } = useTranslation('public-booking');
  const [data, setData] = useState<CustomerDetails>(
    initial ?? { firstName: '', lastName: '' },
  );
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof CustomerDetails, v: string) =>
    setData((prev) => ({ ...prev, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.email && !data.phone) {
      setError(t('details.contactRequired'));
      return;
    }
    setError(null);
    // Trim/normalize
    const cleaned: CustomerDetails = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email?.trim() || undefined,
      phone: data.phone?.trim() || undefined,
      fiscalCode: data.fiscalCode?.trim() || undefined,
      dateOfBirth: data.dateOfBirth || undefined,
      notes: data.notes?.trim() || undefined,
    };
    onSubmit(cleaned);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('details.title')}</h2>
        <p className="text-sm text-gray-500 mt-1">{t('details.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('details.firstName')}>
          <input
            required
            value={data.firstName}
            onChange={(e) => update('firstName', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
        <Field label={t('details.lastName')}>
          <input
            required
            value={data.lastName}
            onChange={(e) => update('lastName', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
        <Field label={t('details.email')}>
          <input
            type="email"
            value={data.email ?? ''}
            onChange={(e) => update('email', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
        <Field label={t('details.phone')}>
          <input
            type="tel"
            value={data.phone ?? ''}
            onChange={(e) => update('phone', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
        <Field label={t('details.fiscalCode')}>
          <input
            value={data.fiscalCode ?? ''}
            onChange={(e) => update('fiscalCode', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md font-mono uppercase focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
        <Field label={t('details.dateOfBirth')}>
          <input
            type="date"
            value={data.dateOfBirth ?? ''}
            onChange={(e) => update('dateOfBirth', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
          />
        </Field>
      </div>

      <Field label={t('details.notes')}>
        <textarea
          rows={3}
          value={data.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
          placeholder={t('details.notesPlaceholder')}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-500"
        />
      </Field>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          ← {t('actions.back')}
        </button>
        <PrimaryButton type="submit">{t('actions.next')}</PrimaryButton>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
